/**
 * Retry mechanism for lazy-loaded component imports.
 *
 * Root cause this guards against: Vite emits content-hashed chunk filenames.
 * After a deployment the old chunk URLs 404, so any browser tab still running
 * the previous build (or holding a cached `index.html`) fails every dynamic
 * import with "Failed to fetch dynamically imported module". Retrying the same
 * stale URL can never succeed, so the app fell through to the ErrorBoundary's
 * "Module Loading Error" screen and required a manual hard refresh.
 *
 * Strategy:
 *  - Retry transient network failures a few times (real flaky connections).
 *  - If the failure looks like a stale-deployment chunk error, perform ONE
 *    full page reload per browser session so the fresh `index.html` (and its
 *    new chunk hashes) is fetched. The one-shot flag in sessionStorage makes
 *    an infinite reload loop impossible; a genuine error still surfaces in the
 *    ErrorBoundary on the second occurrence.
 */

const RETRY_DELAY = 600; // ms
const MAX_RETRIES = 2;
const RELOAD_FLAG = 'lysara:chunk-reload-attempted';

/** True when the error is a dynamic-import/chunk load failure. */
export function isModuleLoadError(error: unknown): boolean {
  const message = (error as { message?: string })?.message ?? String(error ?? '');
  return (
    /dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Failed to fetch/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /Unable to preload CSS/i.test(message)
  );
}

/** One-shot per browser session; returns false if a reload was already tried. */
export function shouldReloadForStaleBuild(): boolean {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return false;
    if (window.sessionStorage.getItem(RELOAD_FLAG)) return false;
    window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

/** Called once the app has successfully booted, so a later deploy can reload again. */
export function clearStaleBuildReloadFlag(): void {
  try {
    window?.sessionStorage?.removeItem(RELOAD_FLAG);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

async function purgeCaches(): Promise<void> {
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* best effort */
  }
}

/** Drop stale caches and request a reload after active work is safe. */
export async function recoverFromStaleBuild(): Promise<never> {
  await purgeCaches();
  const request = new Event('lysara:request-safe-reload', { cancelable: true });
  const hasGuard = !window.dispatchEvent(request);
  if (!hasGuard) window.location.reload();
  // The global build-refresh guard performs the reload when safe. If it is not
  // mounted (for example in development), preserve the original recovery path.
  return new Promise<never>(() => {});
}

export function retryImport<T>(
  importFn: () => Promise<T>,
  retries = MAX_RETRIES
): Promise<T> {
  return importFn().catch(async (error) => {
    if (retries > 0) {
      await new Promise((r) => setTimeout(r, RETRY_DELAY));
      return retryImport(importFn, retries - 1);
    }

    // Exhausted retries. A stale-deployment chunk error is only fixable by
    // fetching a fresh index.html — do that exactly once per session.
    if (isModuleLoadError(error) && shouldReloadForStaleBuild()) {
      console.warn('[retryImport] Stale build detected — reloading once to fetch fresh assets.', error);
      return recoverFromStaleBuild();
    }

    console.error('[retryImport] Module import failed after retries:', {
      message: (error as Error)?.message,
      timestamp: new Date().toISOString(),
    });
    throw error;
  });
}
